import { type ReactNode } from "react";
import {
  Platform, Pressable, RefreshControl, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { Text } from '@/components/common/AppText';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import type { AnalyticsRange } from "@tabbio/types";
import { borderRadius, colors, motion, spacing } from "@tabbio/tokens";
import { ChevronRight } from "lucide-react-native";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import { useFont } from "../../providers/FontProvider";
import { useLocale } from "../../hooks/useLocale";
import { useHaptics } from "../../hooks/useHaptics";
import { NativeButton } from "../common/NativeButton";
import { TabbioAIIcon } from "../common/TabbioAIIcon";
import { JobsSkeleton } from "../jobs/JobsSkeleton";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export const ANALYTICS_RANGES: AnalyticsRange[] = ["7d", "30d", "all"];
export const GROUP_RADIUS = 28;

// ─── Formatters ─────────────────────────────────────────────────────

const hasIntl = typeof Intl !== "undefined";
const hasNumberFormat = hasIntl && typeof Intl.NumberFormat === "function";
const hasDateTimeFormat = hasIntl && typeof Intl.DateTimeFormat === "function";

function formatFallbackCompactNumber(value: number) {
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return String(value);
}

const FALLBACK_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatCompactNumber(value: number, locale: string) {
  if (!hasNumberFormat) {
    return formatFallbackCompactNumber(value);
  }
  try {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
      notation: value >= 1000 ? "compact" : "standard",
    }).format(value);
  } catch {
    return formatFallbackCompactNumber(value);
  }
}

export function formatPercent(value: number, locale: string) {
  if (!hasNumberFormat) {
    return value % 1 === 0 ? String(value) : value.toFixed(1);
  }
  try {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: value % 1 === 0 ? 0 : 1,
    }).format(value);
  } catch {
    return value % 1 === 0 ? String(value) : value.toFixed(1);
  }
}

export function formatAnalyticsDate(
  input: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
) {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return input;
  }

  if (!hasDateTimeFormat) {
    return `${FALLBACK_MONTHS[date.getMonth()]} ${date.getDate()}`;
  }
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return `${FALLBACK_MONTHS[date.getMonth()]} ${date.getDate()}`;
  }
}

function formatFallbackRelativeAnalyticsTime(
  value: number,
  unit: "minute" | "hour" | "day",
  locale: string,
) {
  const arabic = locale.toLowerCase().startsWith("ar");
  const isPast = value <= 0;
  const count = Math.abs(value);

  const unitShort = arabic
    ? unit === "minute"
      ? "د"
      : unit === "hour"
        ? "س"
        : "ي"
    : unit === "minute"
      ? "m"
      : unit === "hour"
        ? "h"
        : "d";

  if (arabic) {
    return isPast ? `قبل ${count} ${unitShort}` : `خلال ${count} ${unitShort}`;
  }

  return isPast ? `${count}${unitShort} ago` : `in ${count}${unitShort}`;
}

export function formatRelativeAnalyticsDate(
  input: string | null,
  locale: string,
) {
  if (!input) {
    return null;
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60_000);
  const diffHours = Math.round(diffMs / 3_600_000);
  const diffDays = Math.round(diffMs / 86_400_000);
  const hasRelativeTimeFormat =
    hasIntl && typeof Intl.RelativeTimeFormat === "function";
  let rtf: Intl.RelativeTimeFormat | null = null;
  if (hasRelativeTimeFormat) {
    try {
      rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    } catch {
      rtf = null;
    }
  }

  const formatWith = (value: number, unit: "minute" | "hour" | "day") => {
    if (rtf) {
      try {
        return rtf.format(value, unit);
      } catch {
        // fall through
      }
    }
    return formatFallbackRelativeAnalyticsTime(value, unit, locale);
  };

  if (Math.abs(diffMinutes) < 60) {
    return formatWith(diffMinutes, "minute");
  }

  if (Math.abs(diffHours) < 24) {
    return formatWith(diffHours, "hour");
  }

  return formatWith(diffDays, "day");
}

export function formatStatusLabel(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function rangeHelperKey(range: AnalyticsRange) {
  return range === "7d"
    ? "analytics.heroSummary7d"
    : range === "30d"
      ? "analytics.heroSummary30d"
      : "analytics.heroSummaryAll";
}

export function applicantPillTone(
  status: string,
): "success" | "warning" | "primary" | "default" {
  const normalized = status.toLowerCase();
  if (normalized.includes("reject") || normalized.includes("dismiss"))
    return "warning";
  if (
    normalized.includes("hire") ||
    normalized.includes("offer") ||
    normalized.includes("accept")
  ) {
    return "success";
  }
  if (
    normalized.includes("shortlist") ||
    normalized.includes("interview") ||
    normalized.includes("star")
  ) {
    return "primary";
  }
  return "default";
}

// ─── Layout helpers ─────────────────────────────────────────────────

export function useAnalyticsShellMetrics() {
  const { width } = useWindowDimensions();
  const isWeb = Platform.OS === "web";
  const maxWidth = isWeb ? 950 : width;
  const pagePadding = isWeb ? 24 : spacing[3];
  return { isWeb, maxWidth, pagePadding };
}

export type MetricTone =
  | "default"
  | "primary"
  | "success"
  | "warning"
  | "danger";

export function toneAccent(tone: MetricTone | undefined): string | null {
  switch (tone) {
    case "primary":
      return colors.primary;
    case "success":
      return colors.success;
    case "warning":
      return "#EA580C";
    case "danger":
      return colors.danger;
    case "default":
    case undefined:
      return null;
  }
}

export function useGroupedSurface() {
  const { theme, isDark } = useTheme();
  return isDark ? theme.surfaceElevated : "rgba(255,255,255,0.82)";
}

// ─── Page shell ─────────────────────────────────────────────────────

export function AnalyticsScreenContainer({
  children,
  refreshing,
  onRefresh,
}: {
  children: ReactNode;
  refreshing?: boolean;
  onRefresh?: () => void;
}) {
  const { theme } = useTheme();

  const refreshControl =
    onRefresh && Platform.OS !== "web" ? (
      <RefreshControl
        refreshing={Boolean(refreshing)}
        onRefresh={onRefresh}
        tintColor={theme.textSecondary}
      />
    ) : undefined;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.background }}
      contentInsetAdjustmentBehavior="automatic"
      showsVerticalScrollIndicator={false}
      refreshControl={refreshControl}
      contentContainerStyle={styles.container}
    >
      {children}
    </ScrollView>
  );
}

// ─── Pill switcher (used for both range + tab) ──────────────────────

export function AnalyticsRangeBar({
  range,
  onChange,
}: {
  range: AnalyticsRange;
  onChange: (range: AnalyticsRange) => void;
}) {
  const { t } = useTranslation("common");
  const tabs = ANALYTICS_RANGES.map((option) => ({
    key: option,
    label: t(`analytics.period${option === "all" ? "All" : option}`),
  }));
  return <AnalyticsTabBar tabs={tabs} activeKey={range} onChange={onChange} />;
}

export function AnalyticsTabBar<TKey extends string>({
  tabs,
  activeKey,
  onChange,
}: {
  tabs: { key: TKey; label: string }[];
  activeKey: TKey;
  onChange: (key: TKey) => void;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const haptics = useHaptics();

  return (
    <View
      style={[styles.tabContainer, { backgroundColor: theme.surfaceSecondary }]}
    >
      {tabs.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <Pressable
            key={tab.key}
            onPress={() => {
              if (active) return;
              haptics.trigger("light");
              onChange(tab.key);
            }}
            style={[
              styles.tab,
              active ? { backgroundColor: theme.background } : null,
            ]}
          >
            <Text
              style={[
                styles.tabText,
                {
                  color: active ? theme.text : theme.textSecondary,
                  fontFamily: fonts.medium,
                },
              ]}
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// ─── Analytics screen header ───────────────────────────────────────

export function AnalyticsHeroPanel({
  title,
  subtitle,
  eyebrow,
  leading,
  children,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  /** Identity mark shown beside the title (e.g. the company's logo). */
  leading?: ReactNode;
  children?: ReactNode;
}) {
  const { theme, isDark } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const writingDirection = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? ("right" as const) : ("left" as const);
  const surface = isDark ? theme.surfaceElevated : theme.background;

  return (
    <View
      style={[
        styles.heroPanel,
        {
          backgroundColor: surface,
          borderColor: theme.borderSubtle,
          shadowOpacity: isDark ? 0.28 : 0.12,
        },
      ]}
    >
      {/* Title block reads like every other screen header: the mark (if any)
          leads, the copy runs along the reading edge. It used to be a centred
          stack under a decorative sparkle, which said nothing about the data
          and pushed the numbers below the fold. */}
      <View
        style={[
          styles.heroTitleRow,
          { flexDirection: isRTL ? "row-reverse" : "row" },
        ]}
      >
        {leading}
        <View style={styles.heroCopy}>
          {eyebrow ? (
            <Text
              style={[
                styles.heroEyebrow,
                {
                  color: theme.textTertiary,
                  fontFamily: fonts.medium,
                  writingDirection,
                  textAlign,
                },
              ]}
              numberOfLines={1}
            >
              {eyebrow}
            </Text>
          ) : null}
          <Text
            style={[
              styles.heroTitle,
              {
                color: theme.text,
                fontFamily: fonts.medium,
                writingDirection,
                textAlign,
              },
            ]}
          >
            {title}
          </Text>
          {subtitle ? (
            <Text
              style={[
                styles.heroSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: fonts.regular,
                  writingDirection,
                  textAlign,
                },
              ]}
              numberOfLines={2}
            >
              {subtitle}
            </Text>
          ) : null}
        </View>
      </View>

      {children ? <View style={styles.heroControls}>{children}</View> : null}
    </View>
  );
}

// ─── Section label (matches jobs/[id].tsx 16/600) ──────────────────

export function AnalyticsSectionLabel({ label }: { label: string }) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();

  return (
    <Text
      style={[
        styles.sectionLabel,
        {
          color: theme.text,
          fontFamily: fonts.medium,
          textAlign: isRTL ? "right" : "left",
        },
      ]}
    >
      {label}
    </Text>
  );
}

// ─── Intro card ────────────────────────────────────────────────────

export function AnalyticsIntroCard({
  title,
  subtitle,
  pill,
}: {
  title: string;
  subtitle?: string;
  pill?: string;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const surface = useGroupedSurface();

  return (
    <View
      style={[
        styles.cardBlock,
        { backgroundColor: surface, borderColor: theme.borderSubtle },
      ]}
    >
      {pill ? <AnalyticsInfoPill label={pill} /> : null}
      <Text
        style={[
          styles.introTitle,
          {
            color: theme.text,
            fontFamily: fonts.medium,
            textAlign: isRTL ? "right" : "left",
          },
        ]}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text
          style={[
            styles.introSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: fonts.regular,
              textAlign: isRTL ? "right" : "left",
            },
          ]}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

// ─── Detail-page hero card (jobs/[id].tsx pattern) ─────────────────

export function AnalyticsDetailHero({
  eyebrow,
  title,
  subtitle,
  pills,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  pills?: ReactNode;
  children?: ReactNode;
}) {
  const { theme, isDark } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const surface = useGroupedSurface();

  return (
    <View
      style={[
        styles.detailHero,
        {
          backgroundColor: surface,
          borderColor: theme.borderSubtle,
          shadowOpacity: isDark ? 0.28 : 0.12,
        },
      ]}
    >
      {eyebrow ? (
        <Text
          style={[
            styles.detailEyebrow,
            {
              color: theme.textTertiary,
              fontFamily: fonts.medium,
              textAlign: isRTL ? "right" : "left",
            },
          ]}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Text
        style={[
          styles.detailTitle,
          {
            color: theme.text,
            fontFamily: fonts.bold,
            textAlign: isRTL ? "right" : "left",
          },
        ]}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text
          style={[
            styles.detailSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: fonts.medium,
              textAlign: isRTL ? "right" : "left",
            },
          ]}
        >
          {subtitle}
        </Text>
      ) : null}
      {pills ? <View style={styles.detailPills}>{pills}</View> : null}
      {children ? <View style={styles.heroControls}>{children}</View> : null}
    </View>
  );
}

// ─── Block primitives ──────────────────────────────────────────────

export type AnalyticsTile = {
  key: string;
  label: string;
  value: string;
  tone?: MetricTone;
  helper?: string;
};

/**
 * One bordered card containing a 2-3 column grid of metric tiles.
 * Tiles are separated by hairline dividers forming a visible grid.
 */
export function AnalyticsMetricGrid({
  metrics,
  columns = 3,
}: {
  metrics: AnalyticsTile[];
  columns?: 2 | 3;
}) {
  const { theme } = useTheme();
  const surface = useGroupedSurface();

  if (!metrics.length) return null;

  return (
    <View
      style={[
        styles.gridCard,
        { backgroundColor: surface, borderColor: theme.borderSubtle },
      ]}
    >
      <View style={styles.gridWrap}>
        {metrics.map((tile, index) => (
          <MetricTile
            key={tile.key}
            tile={tile}
            index={index}
            total={metrics.length}
            columns={columns}
            borderColor={theme.borderSubtle}
          />
        ))}
      </View>
    </View>
  );
}

function MetricTile({
  tile,
  index,
  total,
  columns,
  borderColor,
}: {
  tile: AnalyticsTile;
  index: number;
  total: number;
  columns: 2 | 3;
  borderColor: string;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const accent = toneAccent(tile.tone);

  const col = index % columns;
  const row = Math.floor(index / columns);
  const lastRow = Math.floor((total - 1) / columns);
  const isLastCol = col === columns - 1;
  const isLastRow = row === lastRow;

  return (
    <View
      style={[
        styles.tile,
        {
          width: `${100 / columns}%`,
          borderRightWidth:
            !isLastCol && !isRTL ? StyleSheet.hairlineWidth : 0,
          borderLeftWidth: !isLastCol && isRTL ? StyleSheet.hairlineWidth : 0,
          borderBottomWidth: !isLastRow ? StyleSheet.hairlineWidth : 0,
          borderColor,
        },
      ]}
    >
      <Text
        style={[
          styles.tileLabel,
          {
            color: theme.textSecondary,
            fontFamily: fonts.medium,
            textAlign: isRTL ? "right" : "left",
          },
        ]}
        numberOfLines={1}
      >
        {tile.label}
      </Text>
      <Text
        style={[
          styles.tileValue,
          {
            color: accent ?? theme.text,
            fontFamily: fonts.bold,
            textAlign: isRTL ? "right" : "left",
          },
        ]}
        numberOfLines={1}
      >
        {tile.value}
      </Text>
      {tile.helper ? (
        <Text
          style={[
            styles.tileHelper,
            {
              color: theme.textTertiary,
              fontFamily: fonts.regular,
              textAlign: isRTL ? "right" : "left",
            },
          ]}
          numberOfLines={1}
        >
          {tile.helper}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Each list item rendered as a fully-bordered standalone card with an
 * internal sub-grid of stats. Used for keywords, applicants, jobs,
 * candidates, activity, funnel rows, etc.
 */
export function AnalyticsStatBlock({
  leading,
  title,
  subtitle,
  caption,
  trailing,
  stats,
  onPress,
  showChevron,
}: {
  leading?: ReactNode;
  title: string;
  subtitle?: string;
  caption?: string;
  trailing?: ReactNode;
  stats?: AnalyticsTile[];
  onPress?: () => void;
  showChevron?: boolean;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const haptics = useHaptics();
  const surface = useGroupedSurface();
  const scale = useSharedValue(1);
  const animStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const body = (
    <View>
      <View
        style={[
          styles.blockHeader,
          { flexDirection: isRTL ? "row-reverse" : "row" },
        ]}
      >
        {leading ? <View style={styles.blockLeading}>{leading}</View> : null}
        <View style={styles.blockHeaderCopy}>
          <View
            style={[
              styles.blockTitleLine,
              { flexDirection: isRTL ? "row-reverse" : "row" },
            ]}
          >
            <Text
              style={[
                styles.blockTitle,
                {
                  color: theme.text,
                  fontFamily: fonts.medium,
                  textAlign: isRTL ? "right" : "left",
                },
              ]}
              numberOfLines={1}
            >
              {title}
            </Text>
            {trailing ? <View style={styles.blockTrailing}>{trailing}</View> : null}
          </View>
          {subtitle ? (
            <Text
              style={[
                styles.blockSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: fonts.medium,
                  textAlign: isRTL ? "right" : "left",
                },
              ]}
              numberOfLines={2}
            >
              {subtitle}
            </Text>
          ) : null}
        </View>
        {showChevron ? (
          <ChevronRight
            size={18}
            color={theme.textTertiary}
            strokeWidth={2}
            style={isRTL ? styles.chevronRTL : undefined}
          />
        ) : null}
      </View>

      {stats && stats.length ? (
        <View
          style={[
            styles.blockStats,
            { borderTopColor: theme.borderSubtle },
          ]}
        >
          {stats.map((stat, index) => {
            const isLast = index === stats.length - 1;
            const accent = toneAccent(stat.tone);
            return (
              <View
                key={stat.key}
                style={[
                  styles.statCell,
                  !isLast
                    ? isRTL
                      ? {
                          borderLeftWidth: StyleSheet.hairlineWidth,
                          borderLeftColor: theme.borderSubtle,
                        }
                      : {
                          borderRightWidth: StyleSheet.hairlineWidth,
                          borderRightColor: theme.borderSubtle,
                        }
                    : null,
                ]}
              >
                <Text
                  style={[
                    styles.statLabel,
                    {
                      color: theme.textTertiary,
                      fontFamily: fonts.medium,
                      textAlign: isRTL ? "right" : "left",
                    },
                  ]}
                  numberOfLines={1}
                >
                  {stat.label}
                </Text>
                <Text
                  style={[
                    styles.statValue,
                    {
                      color: accent ?? theme.text,
                      fontFamily: fonts.bold,
                      textAlign: isRTL ? "right" : "left",
                    },
                  ]}
                  numberOfLines={1}
                >
                  {stat.value}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}

      {caption ? (
        <Text
          style={[
            styles.blockCaption,
            {
              color: theme.textTertiary,
              fontFamily: fonts.regular,
              textAlign: isRTL ? "right" : "left",
              marginTop: stats && stats.length ? spacing[3] : 0,
            },
          ]}
          numberOfLines={2}
        >
          {caption}
        </Text>
      ) : null}
    </View>
  );

  const blockStyle = [
    styles.cardBlock,
    { backgroundColor: surface, borderColor: theme.borderSubtle },
  ];

  if (!onPress) {
    return <View style={blockStyle}>{body}</View>;
  }

  return (
    <AnimatedPressable
      onPress={() => {
        haptics.trigger("light");
        onPress();
      }}
      onPressIn={() => {
        scale.value = withTiming(motion.press.scale, {
          duration: motion.duration.instant,
        });
      }}
      onPressOut={() => {
        scale.value = withTiming(1, { duration: motion.duration.fast });
      }}
      style={[animStyle, blockStyle]}
    >
      {body}
    </AnimatedPressable>
  );
}

// ─── Keyword list (single bordered card with per-row bar charts) ───

export type AnalyticsKeywordEntry = {
  key: string;
  keyword: string;
  primaryValue: number;
  primaryDisplay: string;
  secondaryLabel?: string;
  secondaryDisplay?: string;
  caption?: string | null;
};

export function AnalyticsKeywordList({
  keywords,
}: {
  keywords: AnalyticsKeywordEntry[];
}) {
  const { theme } = useTheme();
  const surface = useGroupedSurface();

  if (!keywords.length) return null;

  const max = Math.max(...keywords.map((k) => k.primaryValue), 1);

  return (
    <View
      style={[
        styles.keywordCard,
        { backgroundColor: surface, borderColor: theme.borderSubtle },
      ]}
    >
      {keywords.map((entry, index) => (
        <KeywordRow
          key={entry.key}
          entry={entry}
          ratio={Math.max(entry.primaryValue / max, 0.04)}
          isLast={index === keywords.length - 1}
          borderColor={theme.borderSubtle}
        />
      ))}
    </View>
  );
}

function KeywordRow({
  entry,
  ratio,
  isLast,
  borderColor,
}: {
  entry: AnalyticsKeywordEntry;
  ratio: number;
  isLast: boolean;
  borderColor: string;
}) {
  const { theme, isDark } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();

  return (
    <View
      style={[
        styles.keywordRow,
        !isLast
          ? { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: borderColor }
          : null,
      ]}
    >
      <View
        style={[
          styles.keywordHeader,
          { flexDirection: isRTL ? "row-reverse" : "row" },
        ]}
      >
        <Text
          style={[
            styles.keywordName,
            {
              color: theme.text,
              fontFamily: fonts.medium,
              textAlign: isRTL ? "right" : "left",
            },
          ]}
          numberOfLines={1}
        >
          {entry.keyword}
        </Text>
        <Text
          style={[
            styles.keywordValue,
            { color: theme.text, fontFamily: fonts.bold },
          ]}
          numberOfLines={1}
        >
          {entry.primaryDisplay}
        </Text>
      </View>
      <View
        style={[
          styles.keywordBarTrack,
          { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
        ]}
      >
        <View
          style={[
            styles.keywordBarFill,
            {
              width: `${ratio * 100}%`,
              backgroundColor: colors.primary,
              alignSelf: isRTL ? "flex-end" : "flex-start",
            },
          ]}
        />
      </View>
      {entry.secondaryDisplay || entry.caption ? (
        <View
          style={[
            styles.keywordFooter,
            { flexDirection: isRTL ? "row-reverse" : "row" },
          ]}
        >
          {entry.secondaryDisplay ? (
            <Text
              style={[
                styles.keywordFooterText,
                { color: theme.textTertiary, fontFamily: fonts.medium },
              ]}
              numberOfLines={1}
            >
              {entry.secondaryLabel
                ? `${entry.secondaryLabel} · ${entry.secondaryDisplay}`
                : entry.secondaryDisplay}
            </Text>
          ) : <View />}
          {entry.caption ? (
            <Text
              style={[
                styles.keywordFooterText,
                { color: theme.textTertiary, fontFamily: fonts.regular },
              ]}
              numberOfLines={1}
            >
              {entry.caption}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

// ─── Pills ──────────────────────────────────────────────────────────

export function AnalyticsInfoPill({
  label,
  showAiIcon = true,
}: {
  label: string;
  showAiIcon?: boolean;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();

  return (
    <View
      style={[
        styles.infoPill,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
      ]}
    >
      {showAiIcon ? <TabbioAIIcon size={12} /> : null}
      <Text
        style={[
          styles.infoPillText,
          { color: theme.textSecondary, fontFamily: fonts.medium },
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </View>
  );
}

export function AnalyticsStatusPill({
  label,
  tone = "default",
}: {
  label: string;
  tone?: MetricTone;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const accent = toneAccent(tone);
  const fg = accent ?? theme.textSecondary;
  const bg = accent ? `${accent}16` : theme.surfaceSecondary;

  return (
    <View style={[styles.statusPill, { backgroundColor: bg }]}>
      <Text
        style={[
          styles.statusPillText,
          { color: fg, fontFamily: fonts.medium },
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </View>
  );
}

// ─── Empty / loading / error ───────────────────────────────────────

export function AnalyticsEmpty({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();

  return (
    <View style={styles.centerState}>
      <Text
        style={[
          styles.emptyTitle,
          { color: theme.text, fontFamily: fonts.medium },
        ]}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text
          style={[
            styles.emptySubtitle,
            { color: theme.textSecondary, fontFamily: fonts.regular },
          ]}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

export function AnalyticsLoadingScreen({
  title: _title,
  subtitle: _subtitle,
}: {
  title?: string;
  subtitle?: string;
}) {
  const { theme } = useTheme();
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.background }}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
    >
      <JobsSkeleton variant="feed" count={6} />
    </ScrollView>
  );
}

export function AnalyticsErrorState({
  title,
  subtitle,
  onRetry,
}: {
  title: string;
  subtitle: string;
  onRetry: () => void;
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { t } = useTranslation("common");

  return (
    <View style={styles.centerState}>
      <Text
        style={[
          styles.emptyTitle,
          { color: theme.text, fontFamily: fonts.medium },
        ]}
      >
        {title}
      </Text>
      <Text
        style={[
          styles.emptySubtitle,
          { color: theme.textSecondary, fontFamily: fonts.regular },
        ]}
      >
        {subtitle}
      </Text>
      <NativeButton title={t("analytics.retry")} onPress={onRetry} pill />
    </View>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing[5],
    paddingTop: spacing[3],
    paddingBottom: spacing[10],
    gap: spacing[4],
  },

  // Pill tab switcher (jobs/index.tsx tabContainer)
  tabContainer: {
    flexDirection: "row",
    borderRadius: borderRadius.full,
    padding: 3,
  },
  tab: {
    flex: 1,
    minHeight: 40,
    borderRadius: borderRadius.full,
    alignItems: "center",
    justifyContent: "center",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "600",
  },

  // Section label (jobs/[id].tsx sectionLabel: sentence case 16/600)
  sectionLabel: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: spacing[2],
  },

  // Hero panel: mirrors post-onboarding tour/formSheet visual rhythm.
  heroPanel: {
    borderRadius: 34,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[5],
    alignItems: "stretch",
    gap: spacing[4],
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 14 },
    shadowRadius: 30,
    elevation: 10,
  },
  heroTitleRow: {
    alignItems: "center",
    gap: spacing[3],
  },
  heroEyebrow: {
    maxWidth: "100%",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
  },
  heroCopy: {
    flex: 1,
    gap: 2,
  },
  heroTitle: {
    fontSize: 26,
    lineHeight: 34,
    letterSpacing: 0,
  },
  heroSubtitle: {
    fontSize: 15,
    lineHeight: 21,
    letterSpacing: 0,
  },
  heroControls: {
    alignSelf: "stretch",
    gap: spacing[2],
    marginTop: spacing[1],
  },

  // Generic block / item card: full borders, GROUP_RADIUS
  cardBlock: {
    borderRadius: GROUP_RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[4],
    overflow: "hidden",
    gap: 4,
  },

  // Detail hero (jobs/[id].tsx: 30 radius)
  detailHero: {
    borderRadius: 34,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[6],
    paddingVertical: spacing[6],
    gap: spacing[3],
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.12,
    shadowRadius: 30,
    elevation: 10,
  },
  detailEyebrow: {
    fontSize: 13,
    fontWeight: "500",
  },
  detailTitle: {
    fontSize: 30,
    lineHeight: 36,
    fontWeight: "700",
    letterSpacing: 0,
  },
  detailSubtitle: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "500",
  },
  detailPills: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing[2],
  },

  // Intro card (uses cardBlock surface)
  introTitle: {
    fontSize: 17,
    lineHeight: 22,
    fontWeight: "600",
    letterSpacing: 0,
  },
  introSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },

  // Metric grid: bordered card with hairline-divided tile grid
  gridCard: {
    borderRadius: GROUP_RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  gridWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  tile: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[4],
    minWidth: 0,
    gap: 4,
  },
  tileLabel: {
    fontSize: 12,
    lineHeight: 16,
  },
  tileValue: {
    fontSize: 24,
    lineHeight: 28,
    letterSpacing: 0,
    fontVariant: ["tabular-nums"],
  },
  tileHelper: {
    fontSize: 11,
    lineHeight: 14,
  },

  // Stat block (each list item as its own card)
  blockHeader: {
    alignItems: "flex-start",
    gap: spacing[3],
  },
  blockLeading: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  blockHeaderCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  blockTitleLine: {
    alignItems: "center",
    gap: spacing[2],
  },
  blockTitle: {
    flex: 1,
    fontSize: 17,
    fontWeight: "600",
    letterSpacing: 0,
  },
  blockTrailing: {
    flexShrink: 0,
  },
  blockSubtitle: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
  },
  blockStats: {
    flexDirection: "row",
    marginTop: spacing[3],
    paddingTop: spacing[3],
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  statCell: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: spacing[3],
    gap: 2,
  },
  statLabel: {
    fontSize: 11,
    lineHeight: 14,
  },
  statValue: {
    fontSize: 17,
    lineHeight: 22,
    letterSpacing: 0,
    fontVariant: ["tabular-nums"],
  },
  blockCaption: {
    fontSize: 12,
    lineHeight: 16,
  },
  chevronRTL: {
    transform: [{ scaleX: -1 }],
  },

  // Keyword list (single card with stacked rows + magnitude bar)
  keywordCard: {
    borderRadius: GROUP_RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  keywordRow: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[4],
    gap: spacing[2],
  },
  keywordHeader: {
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
  },
  keywordName: {
    flex: 1,
    fontSize: 16,
    letterSpacing: 0,
  },
  keywordValue: {
    fontSize: 17,
    letterSpacing: 0,
    fontVariant: ["tabular-nums"],
  },
  keywordBarTrack: {
    height: 6,
    borderRadius: 999,
    overflow: "hidden",
  },
  keywordBarFill: {
    height: "100%",
    borderRadius: 999,
  },
  keywordFooter: {
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[2],
  },
  keywordFooterText: {
    fontSize: 12,
    lineHeight: 16,
  },

  // Info pill (CatalogJobCard.infoPill)
  infoPill: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: borderRadius.full,
    borderWidth: StyleSheet.hairlineWidth,
  },
  infoPillText: {
    fontSize: 12,
    fontWeight: "500",
    flexShrink: 1,
  },

  // Status pill (CatalogJobCard.matchBadge: `${color}16` bg)
  statusPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: borderRadius.full,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: "600",
  },

  // Centered states (jobs/index.tsx centerState)
  centerState: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: spacing[6],
    paddingVertical: spacing[8],
    gap: spacing[3],
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    textAlign: "center",
  },
  emptySubtitle: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: "center",
  },
});
